import {
  Box,
  ButtonBase,
  FormControl,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ApartmentIcon from '@mui/icons-material/Apartment';
import TrainIcon from '@mui/icons-material/Train';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import FlightIcon from '@mui/icons-material/Flight';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import { API_GET, DBRequest, useConfig } from '../../../api/api';
import { tokens } from '../../../global/theme/tokens';
import { numberToString } from '../../special/numberConverter';
import ExoAvatar from '../../exo/ExoAvatar';

const cookies = new Cookies();

// The two cars share the car icon and are told apart by a corner badge
// ("P" for private, building for company). All other means have their own icon.
const meansOptions = [
  { value: 'private-car', Icon: DirectionsCarIcon, badge: 'P' },
  { value: 'company-car', Icon: DirectionsCarIcon, BadgeIcon: ApartmentIcon },
  { value: 'train', Icon: TrainIcon },
  { value: 'bus', Icon: DirectionsBusIcon },
  { value: 'taxi', Icon: LocalTaxiIcon },
  { value: 'bicycle', Icon: DirectionsBikeIcon },
  { value: 'airplane', Icon: FlightIcon },
  { value: 'boat', Icon: DirectionsBoatIcon },
  { value: 'on-foot', Icon: DirectionsWalkIcon },
];

// The means icon, with a corner badge for the two car variants.
const MeansIcon = ({ option, selected, colors }) => {
  const Icon = option?.Icon || DirectionsCarIcon;
  const hasBadge = option?.badge || option?.BadgeIcon;

  return (
    <Box sx={{ position: 'relative', lineHeight: 0 }}>
      <Icon sx={{ fontSize: '24px' }} />
      {hasBadge && (
        <Box
          sx={{
            position: 'absolute',
            top: '-6px',
            right: '-9px',
            minWidth: '14px',
            height: '14px',
            px: '2px',
            borderRadius: '7px',
            bgcolor: selected ? colors.info : colors.bgInput,
            color: selected ? '#fff' : colors.text,
            border: `1px solid ${colors.info}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '9px',
            fontWeight: 700,
          }}
        >
          {option.BadgeIcon ? (
            <option.BadgeIcon sx={{ fontSize: '10px' }} />
          ) : (
            option.badge
          )}
        </Box>
      )}
    </Box>
  );
};

// Composite field shown for project events: lets the creator optionally request
// a travel expense for the event. One row: car type | preset | driver.
// Stores meansOfTransportation, travelExpensePresetId and driverUserId.
const TypeEventTravelExpense = ({
  dataCollection,
  field,
  updateValidationOnChange,
  size,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const projectId = dataCollection?.[field.projectKey || 'project']?.id;
  const currencySymbol = config?.currency?.currencySymbol ?? '';

  const [presets, setPresets] = useState([]);
  const [users, setUsers] = useState([]);
  const [carAnchor, setCarAnchor] = useState(null);
  const [driverAnchor, setDriverAnchor] = useState(null);

  const means = dataCollection.meansOfTransportation || '';
  const presetId = dataCollection.travelExpensePresetId || '';
  const driverUserId = dataCollection.driverUserId || '';

  const meansLabels = {
    'private-car': t('Private Car'),
    'company-car': t('Company Car'),
    train: t('Train'),
    bus: t('Bus'),
    taxi: t('Taxi'),
    bicycle: t('Bicycle'),
    airplane: t('Airplane'),
    boat: t('Boat'),
    'on-foot': t('On Foot'),
  };

  // Load users once — but only when the field actually shows (it renders
  // nothing without a project or on an existing event).
  const usersLoaded = useRef(false);
  useEffect(() => {
    if (usersLoaded.current || !config || !projectId || dataCollection.id) return;
    usersLoaded.current = true;
    DBRequest({
      config,
      path: 'users',
      method: API_GET,
      onResponse: (res) => setUsers(res.data || []),
    });
  }, [config, projectId, dataCollection.id]);

  // The selection at response time, not at request time: the employee may pick
  // a preset while the list is still loading.
  const presetIdRef = useRef(presetId);
  presetIdRef.current = presetId;

  // Load presets for the selected project and reset stale selection.
  useEffect(() => {
    setPresets([]);
    if (!projectId || !config || dataCollection.id) return;
    DBRequest({
      config,
      path: `travel-expense-presets?projectId[eq]=${projectId}`,
      method: API_GET,
      onResponse: (res) => {
        const list = res.data || [];
        setPresets(list);
        const currentPresetId = presetIdRef.current;
        if (currentPresetId && !list.some((p) => p.id === currentPresetId)) {
          updateValidationOnChange('travelExpensePresetId', null);
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, config, dataCollection.id]);

  // Sensible defaults so the request is valid as soon as a preset is picked.
  useEffect(() => {
    if (!projectId || dataCollection.id) return;
    if (!dataCollection.meansOfTransportation) {
      updateValidationOnChange('meansOfTransportation', 'private-car');
    }
    if (!dataCollection.driverUserId && cookies.get('userId')) {
      updateValidationOnChange('driverUserId', parseInt(cookies.get('userId'), 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Only available while creating a new event (the request is generated on
  // event creation only).
  if (!projectId || dataCollection.id) return null;

  function presetLabel(preset) {
    const parts = [];
    const address = preset.destinationAddress;
    if (address) {
      const location =
        [address.zipCode, address.city].filter(Boolean).join(' ') ||
        address.street;
      if (location) parts.push(location);
    }
    parts.push(`${numberToString(preset.distanceKilometers, 2)} km`);
    parts.push(`${numberToString(preset.totalPrice, 2)} ${currencySymbol}`);
    return parts.join(' · ');
  }

  const boxSize = size === 'small' ? '40px' : '53px';
  const selectedMeans =
    meansOptions.find((c) => c.value === means) || meansOptions[0];
  const selectedDriver = users.find((u) => u.id === driverUserId);

  const squareSx = {
    width: boxSize,
    height: boxSize,
    minWidth: boxSize,
    borderRadius: '12px',
    border: `1px solid ${colors.info}80`,
    bgcolor: colors.bgInput,
    ':hover': { bgcolor: colors.info + '30' },
  };

  return (
    <Box className="flex flex-col gap-1 w-full">
      <Typography variant="body2" sx={{ opacity: 0.6 }}>
        {field.label || t('Travel Expense Request')}
      </Typography>
      <Box className="flex flex-row gap-2 items-center w-full">
        {/* Means of transportation: shows the selected option, opens a dropdown on click */}
        <ButtonBase
          sx={squareSx}
          title={meansLabels[means]}
          onClick={(e) => setCarAnchor(e.currentTarget)}
        >
          <MeansIcon option={selectedMeans} selected colors={colors} />
        </ButtonBase>
        <Menu
          anchorEl={carAnchor}
          open={Boolean(carAnchor)}
          onClose={() => setCarAnchor(null)}
        >
          {meansOptions.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === means}
              onClick={() => {
                updateValidationOnChange('meansOfTransportation', option.value);
                setCarAnchor(null);
              }}
            >
              <Box className="flex items-center gap-3">
                <MeansIcon
                  option={option}
                  selected={option.value === means}
                  colors={colors}
                />
                <ListItemText>{meansLabels[option.value]}</ListItemText>
              </Box>
            </MenuItem>
          ))}
        </Menu>

        {/* Preset */}
        <FormControl fullWidth size={size}>
          <InputLabel id="event-preset-label">{t('Destination')}</InputLabel>
          <Select
            labelId="event-preset-label"
            value={presetId}
            label={t('Destination')}
            onChange={(e) => {
              const id = e.target.value || null;
              const preset = presets.find((p) => p.id === id);
              const updates = { travelExpensePresetId: id };
              // Pre-fill the event location with the preset's destination address
              // and expose it as a quick address selection suggestion.
              updates.travelExpensePresetAddress =
                preset?.destinationAddress || null;
              if (preset?.destinationAddress) {
                updates[field.locationKey || 'location'] =
                  preset.destinationAddress;
              }
              updateValidationOnChange(null, updates, true);
            }}
          >
            <MenuItem value="">{t('No Select')}</MenuItem>
            {presets.map((preset) => (
              <MenuItem key={preset.id} value={preset.id}>
                {presetLabel(preset)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Driver: just the avatar once selected, opens a dropdown on click */}
        <ButtonBase
          sx={squareSx}
          title={
            selectedDriver
              ? `${selectedDriver.firstName} ${selectedDriver.lastName}`
              : t('Driver')
          }
          onClick={(e) => setDriverAnchor(e.currentTarget)}
        >
          {selectedDriver ? (
            <ExoAvatar
              picture={selectedDriver.picture?.temporaryUrl}
              name={`${selectedDriver.firstName} ${selectedDriver.lastName}`}
              size={boxSize}
            />
          ) : (
            <PersonOutlineIcon sx={{ opacity: 0.6 }} />
          )}
        </ButtonBase>
        <Menu
          anchorEl={driverAnchor}
          open={Boolean(driverAnchor)}
          onClose={() => setDriverAnchor(null)}
          slotProps={{ paper: { sx: { maxHeight: 320 } } }}
        >
          {users.map((user) => (
            <MenuItem
              key={user.id}
              selected={user.id === driverUserId}
              onClick={() => {
                updateValidationOnChange('driverUserId', user.id);
                setDriverAnchor(null);
              }}
            >
              <Box className="flex items-center gap-2">
                <ExoAvatar
                  picture={user.picture?.temporaryUrl}
                  name={`${user.firstName} ${user.lastName}`}
                  size="28px"
                />
                <ListItemText>
                  {user.lastName} {user.firstName}
                </ListItemText>
              </Box>
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  );
};

export default TypeEventTravelExpense;
